import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
  type ColumnDef,
  type ExpandedState,
} from '@tanstack/react-table';
import { Ban, Camera, Check as CheckIcon, Clock, ExternalLink } from 'lucide-react';
import {
  bulkReviewNewItems,
  bulkUpdateNewItemDims,
  fetchDimsSources,
  fetchNewItemVendors,
  fetchNewItemsReview,
  fetchReviewCostSources,
  fetchReviewManufacturers,
  fetchReviewStatusLabels,
  reviewNewItem,
  updateNewItemDims,
} from '../api/new_items';
import type { CostSourceOption, DimsSourceOption, NewItem, NewItemReviewAction } from '../api/new_items';
import type { VendorOption } from '../api/checks';
import { useAuth } from '../context/AuthContext';
import ExportButton from '../components/ExportButton';
import NewItemPayloadPanel, { ReturnBadge } from '../components/NewItemPayloadPanel';
import BulkDimsPopover from '../components/BulkDimsPopover';
import { HeaderActions } from '../components/HeaderActions';
import { LabelBadge } from '../components/badges';
import { Button } from '../components/ui/button';
import { MultiSelect } from '../components/ui/multi-select';
import { DataTable } from '../components/data-table/DataTable';
import { ACTIONS_COL_WIDTH, expandColumn, selectColumn } from '../components/data-table/columns';
import { RowActionButton, RowActionMenu, RowActions, type RowMenuItem } from '../components/data-table/RowActions';
import {
  BulkActionBar,
  ColumnsMenu,
  FilterBar,
  FilterField,
  FilterRow,
  PageHeading,
  ResetFiltersButton,
  TablePagination,
} from '../components/data-table/TableChrome';
import { Input } from '../components/ui/input';
import { fmtDate, fmtDateTime, fmtPrice, fmtVendor } from '../utils/format';
import { takeRows } from '../utils/optimistic';
import { describePartialReview, describeReviewFailure } from '../utils/reviewErrors';
import { useUrlFilters } from '../hooks/useUrlFilters';

const PAGE_SIZES = [50, 100, 250, 500, 1000] as const;

// BOT-434: toast/error labels per review action (skip is split in two).
const REVIEW_ACTION_LABEL: Record<NewItemReviewAction, string> = {
  approved: 'Approve',
  postponed: 'Skip for now',
  skipped: 'Skip forever',
};

const DIMS_SOURCE_STYLES: Record<string, { bg: string; text: string; label: string }> = {
  joes_rules:  { bg: 'bg-blue-100',   text: 'text-blue-800',   label: "Joe's rules" },
  website:     { bg: 'bg-indigo-100',  text: 'text-indigo-800', label: 'Website' },
  ai:          { bg: 'bg-amber-100',   text: 'text-amber-800',  label: 'AI estimate' },
  price_range: { bg: 'bg-red-100',     text: 'text-red-800',    label: 'Price range' },
  manual:      { bg: 'bg-purple-100',  text: 'text-purple-800', label: 'Manual' },
};

function DimsSourceBadge({ source }: { source: string | null }) {
  if (!source) return <span className="text-xs text-zinc-300">—</span>;
  const s = DIMS_SOURCE_STYLES[source] ?? { bg: 'bg-zinc-100', text: 'text-zinc-600', label: source };
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${s.bg} ${s.text}`}>{s.label}</span>;
}

export default function NewItemsReviewPage() {
  const { user } = useAuth();
  const canReview = user?.role === 'reviewer' || user?.role === 'admin';

  const [vendorOptions, setVendorOptions] = useState<VendorOption[]>([]);
  // Vendor lives in the URL so the dashboard's Pending by vendor bars can open
  // this page pre-filtered (?vendor=pac0); Reset clears it from the URL too.
  const [urlFilters, setUrlFilters] = useUrlFilters({ vendor: [] as string[] });
  const vendorCodes = urlFilters.vendor;
  const setVendorCodes = (next: string[]) => setUrlFilters({ vendor: next });
  const [dimsSourceOptions, setDimsSourceOptions] = useState<DimsSourceOption[]>([]);
  const [dimsSources, setDimsSources] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [priceMin, setPriceMin] = useState<number | undefined>();
  const [priceMax, setPriceMax] = useState<number | undefined>();
  const [manufacturerOptions, setManufacturerOptions] = useState<string[]>([]);
  const [manufacturers, setManufacturers] = useState<string[]>([]);
  const [statusLabelOptions, setStatusLabelOptions] = useState<string[]>([]);
  const [statusLabels, setStatusLabels] = useState<string[]>([]);
  const [costSourceOptions, setCostSourceOptions] = useState<CostSourceOption[]>([]);
  const [costSources, setCostSources] = useState<string[]>([]);
  const [returnFilter, setReturnFilter] = useState<string[]>([]);
  const [coreFilter, setCoreFilter] = useState<string[]>([]);
  const [stockMin, setStockMin] = useState<number | undefined>();
  const [stockMax, setStockMax] = useState<number | undefined>();
  const [page, setPage]       = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [items, setItems]     = useState<NewItem[]>([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState<string | null>(null);
  const [busy, setBusy]       = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRowSelection({});
    setExpanded({});
    try {
      const data = await fetchNewItemsReview({
        vendor_codes: vendorCodes.length ? vendorCodes : undefined,
        dims_sources: dimsSources.length ? dimsSources : undefined,
        search: debouncedSearch || undefined,
        price_min: priceMin,
        price_max: priceMax,
        manufacturers: manufacturers.length ? manufacturers : undefined,
        status_labels: statusLabels.length ? statusLabels : undefined,
        cost_sources: costSources.length ? costSources : undefined,
        return_filter: returnFilter.length ? returnFilter[0] : undefined,
        core_filter: coreFilter.length ? coreFilter[0] : undefined,
        stock_min: stockMin,
        stock_max: stockMax,
        page,
        per_page: perPage,
      });
      setItems(data.items);
      setTotal(data.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [vendorCodes, dimsSources, debouncedSearch, priceMin, priceMax, manufacturers, statusLabels, costSources, returnFilter, coreFilter, stockMin, stockMax, page, perPage]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    fetchNewItemVendors().then(setVendorOptions).catch(() => setVendorOptions([]));
    fetchReviewManufacturers().then(setManufacturerOptions).catch(() => setManufacturerOptions([]));
    fetchReviewStatusLabels().then(setStatusLabelOptions).catch(() => setStatusLabelOptions([]));
    fetchReviewCostSources().then(setCostSourceOptions).catch(() => setCostSourceOptions([]));
  }, []);

  useEffect(() => {
    fetchDimsSources().then(setDimsSourceOptions).catch(() => setDimsSourceOptions([]));
  }, []);

  /** Optimistically remove rows and run the call. On a failure — or when a bulk
   *  call moved fewer rows than were sent — reload from the server instead of
   *  restoring the snapshot: a request the gateway timed out may still land on
   *  the server, so "restored" rows could already be decided (BOT-449). */
  async function runOptimistic(ids: number[], call: () => Promise<unknown>, label: string) {
    const { remaining, snapshot } = takeRows(items, ids);
    setActionError(null);
    setActionNotice(null);
    setBusy(true);
    setItems(remaining);
    setTotal(t => t - snapshot.length);
    setRowSelection({});
    try {
      const updated = await call();
      const partial = typeof updated === 'number'
        ? describePartialReview(label, ids.length, updated)
        : null;
      if (partial) {
        setActionNotice(partial);
        await load();
      }
    } catch (e) {
      setActionError(describeReviewFailure(label, e));
      await load();
    } finally {
      setBusy(false);
    }
  }


  async function handleReview(id: number, action: NewItemReviewAction) {
    await runOptimistic([id], () => reviewNewItem(id, action), REVIEW_ACTION_LABEL[action]);
  }

  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter(k => rowSelection[k]).map(Number),
    [rowSelection],
  );

  async function handleBulk(action: NewItemReviewAction) {
    if (selectedIds.length === 0) return;
    await runOptimistic(
      selectedIds,
      () => bulkReviewNewItems(selectedIds, action),
      `Bulk ${REVIEW_ACTION_LABEL[action].toLowerCase()}`,
    );
  }

  const columns = useMemo<ColumnDef<NewItem>[]>(() => {
    const cols: ColumnDef<NewItem>[] = [];
    if (canReview) cols.push(selectColumn<NewItem>());
    cols.push(expandColumn<NewItem>());

    cols.push({
      id: 'sku',
      size: 230,
      enableHiding: false,
      meta: { label: 'SKU' },
      header: 'SKU',
      cell: ({ row }) => (
        <>
          <div className="whitespace-nowrap font-mono text-[12.5px] font-medium">{row.original.sku}</div>
          <div className="max-w-[240px] truncate text-xs text-muted-foreground">
            {row.original.display_name ?? row.original.item_name ?? ''}
          </div>
        </>
      ),
    });

    cols.push({
      id: 'vendor',
      meta: { label: 'Vendor' },
      header: 'Vendor',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-zinc-700">{fmtVendor(row.original.vendor_name)}</span>
      ),
    });

    cols.push({
      id: 'manufacturer',
      meta: { label: 'Manufacturer' },
      header: 'Manufacturer',
      cell: ({ row }) => <span className="whitespace-nowrap text-zinc-700">{row.original.manufacturer ?? '—'}</span>,
    });

    cols.push({
      id: 'productType',
      meta: { label: 'Product type' },
      header: 'Product type',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {row.original.import_payload.product_type ?? '—'}
        </span>
      ),
    });

    cols.push({
      id: 'purchase',
      meta: { label: 'Purchase cost', align: 'right' },
      header: 'Purchase cost',
      cell: ({ row }) => (
        <span className="whitespace-nowrap font-medium">
          {row.original.vendor_price != null ? fmtPrice(row.original.vendor_price) : '—'}
        </span>
      ),
    });

    cols.push({
      id: 'costSource',
      meta: { label: 'Cost source' },
      header: 'Cost source',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {row.original.import_payload.cost_source ?? '—'}
        </span>
      ),
    });

    cols.push({
      id: 'dimsSource',
      meta: { label: 'Dims source' },
      header: 'Dims source',
      cell: ({ row }) => <DimsSourceBadge source={row.original.dims_source} />,
    });

    cols.push({
      id: 'dims',
      meta: { label: 'Dims' },
      header: 'Dims',
      cell: ({ row }) => {
        const p = row.original.import_payload;
        if (p.height_inch == null && p.width_inch == null && p.length_inch == null) {
          return <span className="text-xs text-zinc-300">—</span>;
        }
        return (
          <span className="whitespace-nowrap font-mono text-[12px] text-zinc-700">
            {p.height_inch}×{p.width_inch}×{p.length_inch} / {p.item_weight ?? 0} lb
          </span>
        );
      },
    });

    cols.push({
      id: 'priceCategory',
      meta: { label: 'Price cat.' },
      header: 'Price cat.',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground">
          {row.original.import_payload.item_price_category ?? '—'}
        </span>
      ),
    });

    cols.push({
      id: 'return',
      meta: { label: 'Return' },
      header: 'Return',
      cell: ({ row }) => <ReturnBadge status={row.original.import_payload.return_status} />,
    });

    cols.push({
      id: 'core',
      meta: { label: 'Core', align: 'right' },
      header: 'Core',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-zinc-700">
          {fmtPrice(row.original.import_payload.core_charge_amount)}
        </span>
      ),
    });

    cols.push({
      id: 'vendorStock',
      meta: { label: 'Vendor stock', align: 'right' },
      header: 'Vendor stock',
      cell: ({ row }) => <span className="text-zinc-700">{row.original.vendor_stock ?? '—'}</span>,
    });

    cols.push({
      id: 'status',
      meta: { label: 'Status' },
      header: 'Status',
      cell: ({ row }) => <LabelBadge value={row.original.status_label} />,
    });

    cols.push({
      id: 'scraped',
      meta: { label: 'Scraped' },
      header: 'Scraped',
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-muted-foreground" title={fmtDateTime(row.original.scraped_at)}>
          {fmtDate(row.original.scraped_at)}
        </span>
      ),
    });

    cols.push({
      id: 'actions',
      size: ACTIONS_COL_WIDTH,
      enableHiding: false,
      meta: { label: 'Actions', align: 'right' },
      header: 'Actions',
      cell: ({ row }) => {
        const item = row.original;
        const menuItems: RowMenuItem[] = [];
        if (item.import_payload.url) {
          menuItems.push({ label: 'Open vendor page', icon: ExternalLink, href: item.import_payload.url });
        }
        if (item.screenshot_url) {
          menuItems.push({ label: 'View screenshot', icon: Camera, href: item.screenshot_url });
        }
        return (
          <RowActions>
            {canReview && (
              <>
                <RowActionButton
                  label="Approve — create in NetSuite"
                  icon={CheckIcon}
                  tone="success"
                  disabled={busy}
                  onClick={() => void handleReview(item.id, 'approved')}
                />
                <RowActionButton
                  label="Skip for now — set aside, review later"
                  icon={Clock}
                  disabled={busy}
                  onClick={() => void handleReview(item.id, 'postponed')}
                />
                <RowActionButton
                  label="Skip forever — never create this item"
                  icon={Ban}
                  disabled={busy}
                  onClick={() => void handleReview(item.id, 'skipped')}
                />
              </>
            )}
            <RowActionMenu items={menuItems} />
          </RowActions>
        );
      },
    });

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canReview, busy]);

  const table = useReactTable({
    data: items,
    columns,
    getRowId: row => String(row.id),
    state: { rowSelection, expanded, columnVisibility },
    onRowSelectionChange: setRowSelection,
    onExpandedChange: setExpanded,
    onColumnVisibilityChange: setColumnVisibility,
    getRowCanExpand: () => true,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    initialState: {
      columnPinning: { left: canReview ? ['select', 'expand', 'sku'] : ['expand', 'sku'], right: ['actions'] },
    },
  });

  const totalPages = Math.max(Math.ceil(total / perPage), 1);

  return (
    <>
      <HeaderActions>
        <ExportButton
          resource="new_items_queue"
          filters={{
            vendor_codes: vendorCodes.length ? vendorCodes : undefined,
            dims_sources: dimsSources.length ? dimsSources : undefined,
            search: debouncedSearch || undefined,
            price_min: priceMin,
            price_max: priceMax,
            manufacturers: manufacturers.length ? manufacturers : undefined,
            status_labels: statusLabels.length ? statusLabels : undefined,
            cost_sources: costSources.length ? costSources : undefined,
            return_filter: returnFilter.length ? returnFilter[0] : undefined,
            core_filter: coreFilter.length ? coreFilter[0] : undefined,
            stock_min: stockMin,
            stock_max: stockMax,
          }}
          format="csv"
        />
      </HeaderActions>

      <PageHeading title="New Items — Review" count={total} />

      <p className="mb-4 max-w-3xl text-[13px] text-muted-foreground">
        New item candidates pending review. Check dimensions and source, edit dims if needed,
        then approve to queue for NetSuite creation, skip for now to set it aside this round, or skip forever to dismiss it for good.
      </p>

      <FilterBar>
        <FilterRow>
          <Input
            type="text"
            placeholder="Search SKU or name…"
            className="w-[220px]"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          <MultiSelect
            label="Vendor"
            options={vendorOptions.map(v => ({ value: v.code, label: fmtVendor(v.name) }))}
            selected={vendorCodes}
            onChange={next => { setVendorCodes(next); setPage(1); }}
          />
          <MultiSelect
            label="Dims source"
            options={dimsSourceOptions.map(d => ({
              value: d.dims_source,
              label: DIMS_SOURCE_STYLES[d.dims_source]?.label ?? d.dims_source,
            }))}
            selected={dimsSources}
            onChange={next => { setDimsSources(next); setPage(1); }}
          />
          <MultiSelect
            label="Manufacturer"
            options={manufacturerOptions.map(m => ({ value: m, label: m }))}
            selected={manufacturers}
            onChange={next => { setManufacturers(next); setPage(1); }}
          />
          <MultiSelect
            label="Status"
            options={statusLabelOptions.map(s => ({ value: s, label: s }))}
            selected={statusLabels}
            onChange={next => { setStatusLabels(next); setPage(1); }}
          />
          <MultiSelect
            label="Cost source"
            options={costSourceOptions.map(c => ({ value: c.id, label: c.label }))}
            selected={costSources}
            onChange={next => { setCostSources(next); setPage(1); }}
          />
        </FilterRow>
        <FilterRow className="gap-4">
          <FilterField label="Price $">
            <Input
              type="number"
              min={0}
              placeholder="min"
              className="w-[80px]"
              value={priceMin ?? ''}
              onChange={e => { setPriceMin(e.target.value === '' ? undefined : Number(e.target.value)); setPage(1); }}
            />
            <span className="text-zinc-300">–</span>
            <Input
              type="number"
              min={0}
              placeholder="max"
              className="w-[80px]"
              value={priceMax ?? ''}
              onChange={e => { setPriceMax(e.target.value === '' ? undefined : Number(e.target.value)); setPage(1); }}
            />
          </FilterField>
          <FilterField label="Vendor stock">
            <Input
              type="number"
              min={0}
              placeholder="min"
              className="w-[80px]"
              value={stockMin ?? ''}
              onChange={e => { setStockMin(e.target.value === '' ? undefined : Number(e.target.value)); setPage(1); }}
            />
            <span className="text-zinc-300">–</span>
            <Input
              type="number"
              min={0}
              placeholder="max"
              className="w-[80px]"
              value={stockMax ?? ''}
              onChange={e => { setStockMax(e.target.value === '' ? undefined : Number(e.target.value)); setPage(1); }}
            />
          </FilterField>
          <MultiSelect
            label="Return"
            options={[
              { value: 'non_returnable', label: 'Non-Returnable' },
              { value: 'returnable', label: 'Returnable' },
            ]}
            selected={returnFilter}
            onChange={next => { setReturnFilter(next); setPage(1); }}
          />
          <MultiSelect
            label="Core"
            options={[
              { value: 'yes', label: 'Has core' },
              { value: 'no', label: 'No core' },
            ]}
            selected={coreFilter}
            onChange={next => { setCoreFilter(next); setPage(1); }}
          />
          <div className="ml-auto flex items-center gap-2">
            {(vendorCodes.length > 0 || dimsSources.length > 0 || search !== '' ||
              priceMin != null || priceMax != null || manufacturers.length > 0 || statusLabels.length > 0 ||
              costSources.length > 0 || returnFilter.length > 0 || coreFilter.length > 0 ||
              stockMin != null || stockMax != null) && (
              <ResetFiltersButton onClick={() => {
                setVendorCodes([]); setDimsSources([]); setSearch(''); setDebouncedSearch('');
                setPriceMin(undefined); setPriceMax(undefined);
                setManufacturers([]); setStatusLabels([]);
                setCostSources([]); setReturnFilter([]); setCoreFilter([]);
                setStockMin(undefined); setStockMax(undefined);
                setPage(1);
              }} />
            )}
            <ColumnsMenu table={table} />
          </div>
        </FilterRow>
      </FilterBar>

      {actionNotice && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-[13px] text-amber-800">
          <span>{actionNotice}</span>
          <Button variant="ghost" size="sm" onClick={() => setActionNotice(null)} className="text-amber-700">
            Dismiss
          </Button>
        </div>
      )}

      {actionError && (
        <div className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] text-red-700">
          <span>{actionError}</span>
          <Button variant="ghost" size="sm" onClick={() => setActionError(null)} className="text-red-600">
            Dismiss
          </Button>
        </div>
      )}

      {canReview && selectedIds.length > 0 && (
        <BulkActionBar count={selectedIds.length} total={total} onClear={() => setRowSelection({})}>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => void handleBulk('approved')}>
            <CheckIcon className="h-3.5 w-3.5 text-green-600" strokeWidth={2.5} />
            Approve
          </Button>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => void handleBulk('postponed')}>
            <Clock className="h-3.5 w-3.5 text-sky-600" />
            Skip for now
          </Button>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => void handleBulk('skipped')}>
            <Ban className="h-3.5 w-3.5 text-zinc-500" />
            Skip forever
          </Button>
          <BulkDimsPopover
            selectedCount={selectedIds.length}
            disabled={busy}
            onApply={async (dims) => {
              setBusy(true);
              try {
                await bulkUpdateNewItemDims(selectedIds, dims);
                setRowSelection({});
                void load();
              } catch (e) {
                const msg = e instanceof Error ? e.message : 'request failed';
                setActionError(`Bulk dims update failed. ${msg}`);
              } finally {
                setBusy(false);
              }
            }}
            onApplyAndApprove={async (dims) => {
              setBusy(true);
              try {
                await bulkUpdateNewItemDims(selectedIds, dims);
                await bulkReviewNewItems(selectedIds, 'approved');
                setRowSelection({});
                void load();
              } catch (e) {
                const msg = e instanceof Error ? e.message : 'request failed';
                setActionError(`Apply & approve failed. ${msg}`);
              } finally {
                setBusy(false);
              }
            }}
          />
        </BulkActionBar>
      )}

      <DataTable
        table={table}
        loading={loading}
        error={error}
        emptyMessage="No candidates to review"
        renderExpanded={row => (
          <NewItemPayloadPanel
            payload={row.original.import_payload}
            dimsSource={row.original.dims_source}
            dimsSourceDetail={row.original.dims_source_detail}
            editable={canReview}
            onDimsSave={async (dims) => {
              await updateNewItemDims(row.original.id, dims);
              void load();
            }}
          />
        )}
      />

      <TablePagination
        page={page}
        totalPages={totalPages}
        total={total}
        perPage={perPage}
        pageSizes={PAGE_SIZES}
        selectedCount={selectedIds.length}
        onPageChange={setPage}
        onPerPageChange={n => { setPerPage(n); setPage(1); }}
      />
    </>
  );
}
